import {
    CustomerInfo,
    EntityAccess,
    EntityType,
    FmtpCustomer,
    FmtpError,
    GetParcelSearchParameters,
    LocationInfo,
    PermissionContext,
    PermissionContextMapping,
    ShippingLabelTypes,
    UserPermission,
    v2_parcelSearchParameters,
} from '@lanterds/types';
import {
    FetchedUserPermissions,
    MappedPermission,
    Quantity,
    SelectedPermission,
    SubRow,
} from 'src/components/Views/AdminView/types';
import { omit } from 'lodash';
import { Permissions } from '@lanterds/types';
import { To, useSearchParams } from 'react-router-dom';
import { isAuthorized } from 'src/util/Permissions';
import { GetToken } from 'src/util/GetAuthToken';
import { Buffer } from 'buffer';
import { HierarchyCustomer } from 'src/components/Views/AdminView/types';
import { PermissionDisplayNames } from './constants';
import dayjs from 'dayjs';

export const extractUserSearchInput = (input: Partial<GetParcelSearchParameters>) => {
    return omit(input, ['pageNumber', 'pageSize', 'sortDirection', 'sortOrder', 'sourceOffset']);
};

export const extractParcelSearchInput = (input: v2_parcelSearchParameters) => {
    return omit(input, ['sortDirection', 'sortBy']);
};

export const getCapitalized = (text: string) => {
    return `${text[0].toUpperCase()}${text.slice(1, text.length + 1)}`;
};

export const getShipmentTypeFromContext = (localStorageKey: string, queryParam: string) => {
    const userEmail = getUserEmailFromToken();
    const permissionOptions = [
        Permissions.shippingPortal_transfers,
        Permissions.shippingPortal_returns,
        Permissions.shippingPortal_outbound,
    ];
    const fallbackPermission = permissionOptions.find((option) => isAuthorized(userEmail, option));
    let fallbackPage = '';

    switch (fallbackPermission) {
        case Permissions.shippingPortal_returns:
            fallbackPage = ShippingLabelTypes.Return;
            break;
        case Permissions.shippingPortal_outbound:
            fallbackPage = ShippingLabelTypes.Outbound;
            break;
        default:
            fallbackPage = ShippingLabelTypes.Transfer;
            break;
    }

    const [searchParams] = useSearchParams();
    const storedValue = localStorage.getItem(localStorageKey);
    let paramValue = searchParams.get(queryParam);
    const types = Object.values(ShippingLabelTypes);
    paramValue = paramValue && types.includes(paramValue as ShippingLabelTypes) ? paramValue : null;
    return paramValue ?? storedValue ?? fallbackPage;
};

const buildTree = (items: HierarchyCustomer[], parentId: string | null = null): HierarchyCustomer[] => {
    const children = parentId ? items.filter((item: HierarchyCustomer) => item.parentId === parentId) : items;

    return children.map((child: HierarchyCustomer) => ({
        customerName: child.customerName,
        customerId: child.customerId,
        customerCode: child.customerCode,
        customerParentId: child.parentId,
        children: buildTree(items, child.customerId).sort((a: HierarchyCustomer, b: HierarchyCustomer) =>
            a.customerName.toLocaleLowerCase().localeCompare(b.customerName.toLocaleLowerCase()),
        ),
    }));
};

export const getCustomerInfoHierarchical = (data: CustomerInfo[], includeAll = true) => {
    const rootNodes = data.filter((customer: CustomerInfo) => !customer.parentId);
    const parentAndOrChildNodes = data.filter((customer: CustomerInfo) => customer.parentId);
    const childNodes = buildTree(parentAndOrChildNodes).sort((a: HierarchyCustomer, b: HierarchyCustomer) =>
        a.customerName.toLocaleLowerCase().localeCompare(b.customerName.toLocaleLowerCase()),
    );

    let results = rootNodes.map((rootNode: CustomerInfo) => {
        const parentNode: HierarchyCustomer = {
            customerName: rootNode.customerName,
            customerId: rootNode.customerId,
            customerCode: rootNode.customerCode,
        };
        for (const childNode of childNodes) {
            if (childNode.customerParentId === parentNode.customerId) {
                if (parentNode.children) {
                    parentNode.children.push(childNode);
                } else {
                    parentNode.children = [childNode];
                }
            }
        }
        return parentNode;
    });

    if (includeAll) {
        results = [
            { customerCode: Quantity.all, customerId: Quantity.all, customerName: 'All Customers', parentId: null },
            ...results.sort((a: HierarchyCustomer, b: HierarchyCustomer) =>
                a.customerName.toLocaleLowerCase().localeCompare(b.customerName.toLocaleLowerCase()),
            ),
        ];
    }

    return results;
};

export const mapUserPermissions = (fetchedUserPermissions: FetchedUserPermissions | undefined): MappedPermission[] => {
    if (!fetchedUserPermissions) return [];

    const { permissions, customerAccessList, dealerAccessList } = fetchedUserPermissions;

    const permissionOptionsFromUser = permissions ? (Object.keys(permissions) as (keyof UserPermission)[]) : [];

    const mappedPermissions = permissionOptionsFromUser.map<MappedPermission>((permission) => {
        let subRows: SubRow[] = [];
        const permissionRules = permissions[permission];

        if (permissionRules === true) {
            subRows = [{ type: 'All Permissions', displayName: '' }];
        } else if (Array.isArray(permissionRules)) {
            subRows = permissionRules.map<SubRow>((entity) => {
                const subRowResult: SubRow = {
                    type: entity.type,
                    displayName: '',
                    id: '',
                };

                switch (entity.type) {
                    case EntityType.ACCESS_ALL_CUSTOMERS:
                        subRowResult.displayName = 'All Customers';
                        break;

                    case EntityType.CUSTOMER: {
                        subRowResult.displayName = 'Customer';
                        subRowResult.id = entity.id;

                        const customer = customerAccessList?.find((customer: FmtpCustomer) => customer.fmtpId === entity.id);
                        if (!customer) {
                            throw new FmtpError({ message: 'Failed to find a CUSTOMER.', errorCode: 'SHU002' });
                        }
                        subRowResult.displayName = `${customer.name} (${customer.fmtpId})`;
                        break;
                    }

                    case EntityType.DEALER: {
                        subRowResult.displayName = 'Dealer';
                        subRowResult.id = entity.id;

                        const dealer = dealerAccessList?.find((dealer: LocationInfo) => dealer.accountId === entity.id);
                        if (!dealer) {
                            throw new FmtpError({ message: 'Failed to find a DEALER.', errorCode: 'SHU004' });
                        }
                        const customer = customerAccessList?.find(
                            (customer: FmtpCustomer) => customer.fmtpId === dealer.fmtpCustomerId,
                        );
                        if (!customer) {
                            throw new FmtpError({ message: 'Failed to find parent CUSTOMER for dealer.', errorCode: 'SHU003' });
                        }
                        subRowResult.displayName = `${customer.fmtpId} - ${dealer.locationCode} - ${dealer.name}`;
                        break;
                    }
                }

                return subRowResult;
            });
        }
        const numeric = PermissionContextMapping[permission];
        const context = PermissionContext[numeric] as keyof typeof PermissionContext;

        return {
            permission,
            subRows,
            displayName: PermissionDisplayNames[permission],
            context,
        };
    });

    return mappedPermissions
        .filter((permissions) => permissions.displayName && permissions.displayName.length > 0)
        .sort((a, b) => a.displayName.toLocaleLowerCase().localeCompare(b.displayName.toLocaleLowerCase()));
};

export const filterPermissionOptions = (
    userAdminPermissions: Permissions,
    usersPermissions?: UserPermission | null,
): Permissions[] => {
    if (usersPermissions) {
        const availablePermissionsToAssign = Object.keys(userAdminPermissions) as (keyof UserPermission)[];

        return availablePermissionsToAssign.filter((permissionName) => {
            const permission = usersPermissions ? usersPermissions[permissionName] : null;
            if (permission === true) {
                return false;
            } else if (Array.isArray(permission)) {
                return permission?.find((permission: EntityAccess) => permission.type !== EntityType.ACCESS_ALL_CUSTOMERS);
            } else {
                return true;
            }
        });
    } else {
        return Object.keys(userAdminPermissions) as (keyof UserPermission)[];
    }
};

export const mapPermissionOptions = (permissions: Permissions[]): SelectedPermission[] => {
    return permissions
        .map((permission) => {
            return {
                displayName: PermissionDisplayNames[permission],
                name: permission,
                context: PermissionContextMapping[permission as Permissions],
            };
        })
        .sort((a, b) => a?.displayName?.toLocaleLowerCase().localeCompare(b?.displayName?.toLocaleLowerCase()));
};

export const getUserEmailFromToken = () => {
    const token = GetToken();
    const parsedToken = JSON.parse(Buffer.from(token.split('.')[1], 'base64').toString());
    return parsedToken.email;
};

export const getHasAllCustomersAccess = (userPermissions: UserPermission) => {
    if (userPermissions.userManagement) {
        const entities = userPermissions.userManagement;
        if (
            entities === true ||
            (Array.isArray(entities) && entities.length && entities[0].type === EntityType.ACCESS_ALL_CUSTOMERS)
        ) {
            return true;
        }
        return false;
    }
};

export const sortByDate = (data: any[], sortByProperty: string, sortDirection: 'ASC' | 'DESC') => {
    const sortedData = [...data];

    if (sortDirection === 'ASC') {
        sortedData.sort((a, b) => {
            return dayjs(a[sortByProperty]).valueOf() - dayjs(b[sortByProperty]).valueOf();
        });
    } else {
        sortedData.sort((a, b) => {
            return dayjs(b[sortByProperty]).valueOf() - dayjs(a[sortByProperty]).valueOf();
        });
    }

    return sortedData;
};

export const addSessionId = (path: To) => {
    const timeStamp = String(Math.round(new Date().getTime()));

    if (typeof path === 'string') {
        if (path.includes('?')) {
            return `${path}&sessionId=${timeStamp}`;
        } else {
            return `${path}?sessionId=${timeStamp}`;
        }
    } else {
        return { ...path, search: `${path.search}&sessionId=${timeStamp}` };
    }
};
